"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { usePostHog } from "posthog-js/react";
import useCookieConsent from "@/hooks/useCookieConsent";

/**
 * Relie la session Believemy à PostHog :
 * - identify() à la connexion → un même utilisateur = une seule personne,
 *   quel que soit l'appareil (visiteurs uniques, funnels et rétention fiables)
 * - reset() à la déconnexion (ou session expirée/révoquée) → la navigation
 *   suivante, ou un autre compte sur le même poste, n'est pas attribuée à
 *   l'ancien utilisateur
 *
 * Le reset se base sur l'état identifié de PostHog (et non sur une transition
 * connecté → déconnecté observée en mémoire) : il fonctionne donc aussi quand
 * la déconnexion passe par une redirection pleine page.
 */
export default function PostHogIdentity() {
    const { data: session, status } = useSession();
    const posthog = usePostHog();
    // Identifier la personne (e-mail, pseudo) exige l'accord « mesure
    // d'audience » : sans lui, PostHog reste anonyme et sans cookie.
    const analyticsAllowed = useCookieConsent()?.analytics === true;

    useEffect(() => {
        if (!posthog || !posthog.__loaded) return;

        if (
            analyticsAllowed &&
            status === "authenticated" &&
            session?.user?.userId
        ) {
            const distinctId = String(session.user.userId);
            const personProperties = {
                email: session.user.email,
                pseudo: session.user.pseudo,
                name: session.user.name || session.user.firstName,
            };
            if (posthog.get_distinct_id() !== distinctId) {
                posthog.identify(distinctId, personProperties);
            } else if (typeof posthog.setPersonProperties === "function") {
                // Déjà identifié sur cet appareil : rafraîchit les propriétés
                // si elles ont changé (pseudo/email modifiés dans les
                // réglages) ; posthog-js déduplique lui-même les appels
                // strictement identiques
                posthog.setPersonProperties(personProperties);
            }
            return;
        }

        if (
            status === "unauthenticated" &&
            typeof posthog._isIdentified === "function" &&
            posthog._isIdentified()
        ) {
            // "unauthenticated" peut être transitoire : next-auth traite tout
            // échec du refetch de session (blip réseau, réveil de laptop, 5xx)
            // comme une session nulle. On confirme donc la déconnexion auprès
            // de l'endpoint de session avant de réinitialiser l'identité,
            // sinon on perdrait l'identité et le cache des feature flags
            // (variante A/B qui bascule) sur une simple coupure wifi.
            let cancelled = false;
            fetch("/api/auth/session")
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    // NextAuth renvoie {} quand il n'y a réellement pas de
                    // session ; data null = erreur réseau/serveur → on ne
                    // touche pas à l'identité
                    if (!cancelled && data && !data.user) {
                        posthog.reset();
                    }
                })
                .catch(() => {
                    // Déconnexion non confirmée : on garde l'identité
                });
            return () => {
                cancelled = true;
            };
        }
    }, [status, session, posthog, analyticsAllowed]);

    return null;
}
